import { SIMILARITY_SEARCH_HOW_IT_WORKS_VIDEO_URL } from '@aibees/elephant/constants';
import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { SearchType } from '@aibees/elephant/types';
import { Icon, VideoModal } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import useTableDataFetchingStore from '@aibees/shared/store/table-data-fetching/table-data-fetching-store';
import { classnames } from '@aibees/shared/utils';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import { ScrollableArea } from './components/scrollable-area';
import { TagWrapper } from './components/tag-wrapper';
import * as Styled from './styles';
import * as SX from './sx';
import type { SimilaritySearchInputProps as Props } from './types';

export const SimilaritySearchInput = memo(
  ({ className, values, onDelete, addDialogState }: Props) => {
    const type = useSearchTypeFromRoute();
    const isCompany = type === SearchType.Company;
    const { isLoading } = useTableDataFetchingStore();
    const videoState = useBoolean();

    const handleVideoPaperClick = (e: React.MouseEvent) => {
      e.preventDefault();
      videoState.onTrue();
    };

    return (
      <Styled.Wrapper
        className={classnames('SimilaritySearchInput', className)}
        sx={SX.root}
      >
        <Box sx={SX.leftBox}>
          <Styled.InputWrapper
            className={classnames('InputWrapper')}
            onClick={addDialogState.onTrue}
          >
            <Stack direction="row" alignItems="center" flexWrap="wrap">
              {values.length === 0 && !isLoading && (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Icon
                    name={isCompany ? 'business' : 'linkedin-contain'}
                    sxx={SX.placeholderIcon}
                    size={18}
                  />

                  <Typography color="grey.400" variant="body3">
                    {isCompany
                      ? 'e.g. Nike, nike.com, linkedin.com/company/nike'
                      : 'Enter Name or Paste the LinkedIn URL here to find similar profiles'}
                  </Typography>
                </Stack>
              )}
              <ScrollableArea values={values}>
                {isLoading && (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <CircularProgress sx={SX.loadingSpinner} size={18} />
                    <Typography
                      variant="body2"
                      fontWeight={400}
                      color="grey.400"
                    >
                      Loading...
                    </Typography>
                  </Stack>
                )}
                <TagWrapper onDelete={onDelete} value={values} />
              </ScrollableArea>
            </Stack>
          </Styled.InputWrapper>
        </Box>
        <Box sx={SX.rightBox}>
          <Paper
            sx={SX.video(isCompany)}
            onClick={isCompany ? handleVideoPaperClick : undefined}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <Stack direction="column">
                <Typography variant="body4">How it works?</Typography>
                <Typography variant="body5" fontWeight={400} color="grey.500">
                  1 Min. Video{' '}
                </Typography>
              </Stack>
              <Stack sx={SX.iconBox}>
                <Stack justifyContent="center" alignItems="center">
                  <Icon name="play" color="primary.400" size={20} />
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Box>
        <VideoModal
          state={videoState}
          videoUrl={SIMILARITY_SEARCH_HOW_IT_WORKS_VIDEO_URL}
          title="Similarity Search — How it works"
        />
      </Styled.Wrapper>
    );
  }
);

SimilaritySearchInput.displayName = 'SimilaritySearchInput';
